import React, { useState, useEffect, useMemo } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MenuItem from "@mui/material/MenuItem";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogContentText from "@mui/material/DialogContentText";
import PopupState, { bindTrigger } from "material-ui-popup-state";

import { isReactComponent } from "../../utils/detect";
import { filterDeletedCustomFields } from "../../utils/filterDeletedCustomFields";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 0;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  // padding: grid * 2,
  // margin: `0 0 ${grid}px 0`,
  marginTop: "4px",
  // change background colour if dragging
  background: isDragging ? "#d5d5d5" : "#fafafa",
  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = isDraggingOver => ({
  width: "100%",
  // background: isDraggingOver ? "lightblue" : undefined,
  padding: grid,
});
export default function Order({ fields: initHeaders, onChange, reorderFields, hideFieldIds }) {
  const [fields, setFields] = useState(initHeaders);
  const [search, setSearch] = useState("");

  const filteredFields = useMemo(() => {
    return filterDeletedCustomFields(fields);
  }, [fields]);

  useEffect(() => {
    setFields(initHeaders);
  }, [initHeaders]);

  const handleUpdate = newFields => onChange({ fields: newFields });
  const normalizedSearch = search.trim().toLowerCase();

  const onDragEnd = result => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newHeaders = reorder(filteredFields, result.source.index, result.destination.index);
    setFields(newHeaders);
    handleUpdate(newHeaders);
  };

  const handleMoveToTop = fieldId => {
    const index = filteredFields.findIndex(field => field.id === fieldId);
    if (index === -1) return;

    const newHeaders = reorder(filteredFields, index, 0);
    setFields(newHeaders);
    handleUpdate(newHeaders);
  };

  const renderFieldsList = () => (
    <>
      <Box sx={{ position: "sticky", top: 0, zIndex: 1, background: "#f4f6f8", pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Find a column..."
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </Box>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              {filteredFields.map((field, index) => {
                const label = isReactComponent(field.label) ? "" : String(field.label || "");
                const matchesSearch = !normalizedSearch ||
                  label.toLowerCase().includes(normalizedSearch) ||
                  field.id.toLowerCase().includes(normalizedSearch);
                const showField = field.visible &&
                  !field.inactive &&
                  !hideFieldIds?.has(field.id) &&
                  matchesSearch;

                return (
                  <Draggable
                    key={field.id}
                    draggableId={field.id}
                    index={index}
                    isDragDisabled={Boolean(normalizedSearch)}
                  >
                    {(provided, snapshot) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        disablePadding
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                        sx={{ p: 0, display: showField ? "block" : "none" }}
                      >
                        <ListItemButton>
                          <Box display="flex" alignItems="center" width="100%" gap={1}>
                            <Box flexGrow={1}>
                              {field.label && isReactComponent(field.label) ? (
                                <field.label />
                              ) : (
                                field.label
                              )}
                            </Box>
                            {normalizedSearch && (
                              <Button
                                size="small"
                                onClick={event => {
                                  event.stopPropagation();
                                  handleMoveToTop(field.id);
                                }}
                                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                              >
                                Move to top
                              </Button>
                            )}
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );

  if (reorderFields) {
    return renderFieldsList();
  }

  return (
    <PopupState variant="popover" popupId="reorder-popup-menu">
      {popupState => (
        <>
          <MenuItem {...bindTrigger(popupState)}>
            <SyncAltIcon
              sx={{ transform: "rotate(180deg)" }}
              fontSize="small"
              htmlColor="#546e7a"
            />
            <Box mr={1} />
            <Typography
              display={{ xs: "none", sm: "block" }}
              fontWeight={500}
              color="textSecondary"
              variant="body2"
            >
              Reorder Columns
            </Typography>
          </MenuItem>

          <Dialog open={popupState.isOpen} maxWidth="sm" fullWidth onClose={popupState.close}>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f4f6f8",
              }}
            >
              <Box width={48} />
              <Typography color="textSecondary" variant="h5" fontWeight={500} align="center">
                Reorder Columns
              </Typography>
              <IconButton onClick={popupState.close}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ background: "#f4f6f8" }}>
              <DialogContentText>{renderFieldsList()}</DialogContentText>
            </DialogContent>
          </Dialog>
        </>
      )}
    </PopupState>
  );
}
